import { Logger } from '@gitbook/util-debug';
import { isPromise } from '@gitbook/util-ts';

import {
    type PaletteCommandDef,
    PaletteCommandType,
    type PaletteDef,
    type PaletteGroupDef,
} from './types';

const logger = Logger('palette');

/**
 * Create a palette from scratch.
 */
export function createPalette(props: Partial<PaletteDef>): PaletteDef {
    return {
        groups: [],
        query: {
            text: props.search?.initialValue ?? '',
        },
        ...props,
    };
}

/**
 * Execute a command and returns the new palette if any.
 */
export function executeCommandInPalette(
    palette: PaletteDef,
    command: string | PaletteCommandDef
): PaletteDef | null | Promise<PaletteDef | null> {
    const commandInst =
        typeof command === 'string' ? resolveCommandInPalette(palette, command) : command;

    if (!commandInst) {
        logger.warning(`Command not found "${command}"`);
        return null;
    }

    if (commandInst.trigger) {
        const result = commandInst.trigger();
        function setPosition(result: PaletteDef | void): PaletteDef | null {
            if (!result) {
                return null;
            }

            return {
                ...result,
                // Preserve positioning from the current palette when available.
                position: palette.position ?? result.position,
            };
        }

        if (!isPromise(result)) {
            return setPosition(result);
        }

        return result.then(setPosition);
    }

    return null;
}

/**
 * Update the selected state of a checkbox command in a palette group.
 */
export function updatePaletteCheckboxCommandSelected(
    palette: PaletteDef,
    group: PaletteGroupDef,
    key: string,
    selected: boolean
): PaletteDef {
    return {
        ...palette,
        groups: palette.groups.map((currentGroup) => {
            if (currentGroup !== group) {
                return currentGroup;
            }

            return {
                ...currentGroup,
                commands: updateCheckboxCommandsSelected(currentGroup.commands, key, selected),
            };
        }),
    };
}

function updateCheckboxCommandsSelected(
    commands: PaletteGroupDef['commands'],
    key: string,
    selected: boolean
): PaletteGroupDef['commands'] {
    if (typeof commands === 'function') {
        return commands.length === 0
            ? () => updateStaticCheckboxCommandsSelected(commands(''), key, selected)
            : (query: string) =>
                  updateStaticCheckboxCommandsSelected(commands(query), key, selected);
    }

    return updateStaticCheckboxCommandsSelected(commands, key, selected);
}

function updateStaticCheckboxCommandsSelected(
    commands: PaletteCommandDef[],
    key: string,
    selected: boolean
): PaletteCommandDef[] {
    return commands.map((item) => {
        if (item.key === key && item.type === PaletteCommandType.CHECKBOX) {
            return { ...item, selected };
        }

        return item;
    });
}

/**
 * Resolve a command by its ID in a palette.
 */
function resolveCommandInPalette(
    palette: PaletteDef,
    commandKey: string
): PaletteCommandDef | null {
    for (const group of palette.groups) {
        if (Array.isArray(group.commands)) {
            for (const command of group.commands) {
                if (command.key === commandKey) {
                    return command;
                }
            }
        }
    }

    return null;
}

/**
 * Transform to type a text in a palette query.
 */
export function typeQueryInPalette(palette: PaletteDef, text: string): PaletteDef {
    if (palette.query?.text === text) {
        return palette;
    }

    return {
        ...palette,
        query: {
            ...palette.query,
            text,
        },
    };
}

/**
 * Return true if a palette should have a search input.
 */
export function hasPaletteSearchInput(palette: PaletteDef): boolean {
    return Boolean(palette.search);
}
